import { useStore } from '@/hooks/useStore';
import { cn } from '@/utils/utils';
import { CloudUpload } from 'lucide-react';
import { flowResult } from 'mobx';
import { useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormMessage } from '../common/Form';
import { Input } from '../common/Input';
import { observer } from 'mobx-react-lite';
import { DEFAULT_IMAGE_LIMIT_SIZE, DEFAULT_PDF_LIMIT_SIZE } from '@/configs/constants';

interface FileUploadProps {
    fieldName: string,
    acceptUpload?: string,
    form: UseFormReturn<any>,
    className?: string,
    wrapperClass?: string,
    disabled?: boolean,
    required: boolean,
    placeholder?: string,
    handleUploadFile: Function
}

export default observer(function FileUpload({
    fieldName,
    acceptUpload = 'image/*',
    form,
    className,
    wrapperClass,
    disabled = false,
    required = false,
    placeholder = '',
    handleUploadFile }: FileUploadProps) {
    const { formState: { errors } } = form;
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const {
        fileStore: { uploadFile }, modalStore: { showAlertModal }
    } = useStore();

    // Handle image change
    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            let fileType = file.type;

            if (acceptUpload.includes('image') && acceptUpload.includes('pdf')) {
                if (!(fileType.includes('image') || fileType.includes('pdf'))) {
                    showAlertModal({
                        id: 'error',
                        type: 'error',
                        content: t('messages.only_upload_pdf_image')
                    });
                    e.target.value = '';
                    return;
                }
            } else if (acceptUpload.includes('image')) {
                if (!fileType.includes('image')) {
                    showAlertModal({
                        id: 'error',
                        type: 'error',
                        content: t('messages.only_upload_image')
                    });
                    e.target.value = '';
                    return;
                }
            }

            const maxSizeBytes = fileType.includes('image') ? DEFAULT_IMAGE_LIMIT_SIZE * 1024 * 1024 : fileType.includes('pdf') ? DEFAULT_PDF_LIMIT_SIZE * 1024 * 1024 : null;
            if (maxSizeBytes && file.size > maxSizeBytes) {
                showAlertModal({
                    id: 'error',
                    type: 'error',
                    content: t('messages.exceed_size_limit', { size: maxSizeBytes / (1024 * 1024) })
                });
                e.target.value = '';
                return;
            }

            const res = await flowResult(uploadFile({ files: file }));

            if (res?.ok) {
                if (res.data?.length > 0) {
                    handleUploadFile(res.data);
                }
            }
        }
        e.target.value = '';
    };
    return (
        <div className={cn('w-full flex flex-col', wrapperClass)}>
            <Input
                type='file'
                accept={acceptUpload}
                ref={fileInputRef}
                onChange={handleImageChange}
                className='hidden'
            />
            <button
                type='button'
                disabled={disabled}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                    'w-full h-fit flex flex-col items-center justify-center rounded-lg border border-input transition-colors duration-300',
                    className,
                    !!errors[fieldName] && 'border-primaryWeb',
                    disabled ? 'cursor-not-allowed' : 'hover:border-primaryWeb'
                )}
            >
                <div className='flex flex-col items-center justify-center h-48'>
                    <CloudUpload className='size-10 text-primaryWeb' />
                    <p className='mt-1 text-placeholder px-2'>
                        {placeholder || t('field.choose_image')}
                        {required && <span className='text-primaryWeb'>*</span>}
                    </p>
                </div>
            </button>
            {errors[fieldName] && (
                <FormMessage className='mt-0.5'>
                    {errors[fieldName]?.message as string}
                </FormMessage>
            )}
        </div>
    );
});
